// ABOUTME: Custom hook for managing file upload state and operations
// ABOUTME: Handles file state, progress tracking, and upload coordination

import { useState, useCallback, useRef, useMemo } from 'react';
import { FileItem, UploadStats, FileStatus as FileStatusType } from '../types/types';
import { FILE_STATUS } from '../utils/constants';
import sanitize from 'sanitize-s3-objectkey';
// Generate unique ID without external dependencies
const generateId = () => `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

interface UseFileUploadManagerOptions {
  maxFiles?: number;
  autoGenerateId?: boolean;
  onStatsChange?: (stats: UploadStats) => void;
}

export const useFileUploadManager = (initialFiles: FileItem[] = [], options: UseFileUploadManagerOptions = {}) => {
  const { maxFiles = 20, autoGenerateId = true, onStatsChange } = options;

  const [files, setFiles] = useState<FileItem[]>(initialFiles);
  const uploadControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Sanitize filename for S3
  const sanitizeFileName = useCallback((fileName: string): string => {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) return sanitize(fileName);

    const nameWithoutExt = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex + 1).toLowerCase();
    const sanitizedName = nameWithoutExt.split('.').join('-');

    return sanitize(`${sanitizedName}.${extension}`);
  }, []);

  // Calculate upload statistics
  const stats = useMemo<UploadStats>(() => {
    const uploadedFiles = files.filter(f => f.status === FILE_STATUS.UPLOADED).length;
    const failedFiles = files.filter(f => f.status === FILE_STATUS.ERROR).length;
    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    const uploadedSize = files.filter(f => f.status === FILE_STATUS.UPLOADED).reduce((sum, f) => sum + f.size, 0);

    const newStats: UploadStats = {
      totalFiles: files.length,
      uploadedFiles,
      failedFiles,
      totalSize,
      uploadedSize,
    };

    // Call onStatsChange if provided
    if (onStatsChange) {
      onStatsChange(newStats);
    }

    return newStats;
  }, [files, onStatsChange]);

  // Add files to the upload queue
  const addFiles = useCallback(
    (newFiles: File[]): FileItem[] => {
      const availableSlots = maxFiles - files.length;
      const filesToAdd = newFiles.slice(0, availableSlots);

      const fileItems: FileItem[] = filesToAdd.map(file => {
        const sanitizedName = sanitizeFileName(file.name);

        return {
          id: autoGenerateId ? generateId() : sanitizedName,
          name: sanitizedName,
          originalName: file.name,
          file,
          status: FILE_STATUS.READY as FileStatusType,
          progress: 0,
          size: file.size,
          type: file.type,
        };
      });

      setFiles(prev => [...prev, ...fileItems]);
      return fileItems;
    },
    [files.length, maxFiles, autoGenerateId, sanitizeFileName],
  );

  // Update file status
  const updateFileStatus = useCallback((fileId: string, updates: Partial<FileItem>) => {
    setFiles(prev => prev.map(file => (file.id === fileId ? { ...file, ...updates } : file)));
  }, []);

  // Update file progress
  const updateFileProgress = useCallback(
    (fileId: string, progress: number) => {
      updateFileStatus(fileId, { progress: Math.min(100, Math.max(0, progress)) });
    },
    [updateFileStatus],
  );

  // Remove a file
  const removeFile = useCallback((fileId: string) => {
    // Cancel upload if in progress
    const controller = uploadControllersRef.current.get(fileId);
    if (controller) {
      controller.abort();
      uploadControllersRef.current.delete(fileId);
    }

    setFiles(prev => prev.filter(file => file.id !== fileId));
  }, []);

  // Cancel an upload
  const cancelUpload = useCallback(
    (fileId: string) => {
      const controller = uploadControllersRef.current.get(fileId);
      if (controller) {
        controller.abort();
        uploadControllersRef.current.delete(fileId);
      }

      updateFileStatus(fileId, {
        status: FILE_STATUS.CANCELLED as FileStatusType,
        progress: 0,
      });
    },
    [updateFileStatus],
  );

  // Retry a failed upload
  const retryUpload = useCallback(
    (fileId: string) => {
      const file = files.find(f => f.id === fileId);
      if (!file || file.status !== FILE_STATUS.ERROR) return;

      updateFileStatus(fileId, {
        status: FILE_STATUS.READY as FileStatusType,
        progress: 0,
        error: undefined,
        retryCount: (file.retryCount || 0) + 1,
      });
    },
    [files, updateFileStatus],
  );

  // Register an abort controller for a file
  const registerAbortController = useCallback((fileId: string, controller: AbortController) => {
    uploadControllersRef.current.set(fileId, controller);
  }, []);

  // Clear all files
  const clearAll = useCallback(() => {
    // Cancel all ongoing uploads
    uploadControllersRef.current.forEach(controller => controller.abort());
    uploadControllersRef.current.clear();

    setFiles([]);
  }, []);

  // Get files by status
  const getFilesByStatus = useCallback(
    (status: FileStatusType) => {
      return files.filter(file => file.status === status);
    },
    [files],
  );

  // Check if can add more files
  const canAddFiles = useMemo(() => files.length < maxFiles, [files.length, maxFiles]);

  return {
    files,
    stats,
    addFiles,
    removeFile,
    updateFileStatus,
    updateFileProgress,
    cancelUpload,
    retryUpload,
    clearAll,
    getFilesByStatus,
    registerAbortController,
    canAddFiles,
    maxFiles,
  };
};
